import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Persists game state to sessionStorage.
 *
 * - key:       unique key per game instance (e.g. 'game_memory_easy_images-images')
 * - onRestore: called once on mount if a saved state exists
 *
 * Returns:
 * - save(state): call whenever meaningful state changes
 * - clear():     call when game completes or user intentionally exits
 * - hasSaved:    boolean — whether a saved state existed on mount
 */
export function useGameSave<T>(
  key: string,
  onRestore: (saved: T) => void,
): {
  save: (state: T) => void
  clear: () => void
  hasSaved: boolean
} {
  // Lazy initializer — reads sessionStorage once synchronously before first render.
  const [savedValue] = useState<T | null>(() => {
    try {
      const raw = sessionStorage.getItem(key)
      return raw !== null ? (JSON.parse(raw) as T) : null
    } catch {
      // sessionStorage may be disabled or the stored JSON may be malformed.
      return null
    }
  })

  const hasSaved = savedValue !== null

  // Hold onRestore in a ref — it's only called once on mount, so the initial value is enough.
  const onRestoreRef = useRef(onRestore)

  useEffect(() => {
    if (savedValue !== null) {
      onRestoreRef.current(savedValue)
    }
  }, [savedValue]) // savedValue never changes — effect runs once on mount

  const save = useCallback(
    (state: T) => {
      try {
        sessionStorage.setItem(key, JSON.stringify(state))
      } catch {
        // Ignore — storage quota may be exceeded or storage may be disabled.
      }
    },
    [key],
  )

  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(key)
    } catch {
      // Ignore
    }
  }, [key])

  return { save, clear, hasSaved }
}
